import { useState, useEffect, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AxiosError } from "axios";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { CTA } from "@/lib/cta";
import { captureSignupPlan } from "@/lib/signup-plan";
import { captureSignupRef } from "@/lib/signup-ref";

const STORE_TYPES = [
  "Restaurant",
  "Deli",
  "Bodega",
  "Cafe",
  "Liquor Store",
  "Convenience Store",
  "Smoke Shop",
  "Bakery",
  "Bar/Lounge",
  "Food Truck",
  "Pizzeria",
  "Other",
] as const;

// Password rules mirror the backend RegisterRequest validator (F15). Keeping
// them in sync here catches weak passwords before a round-trip and the
// backend's generic "Registration failed" response.
function passwordIssue(pw: string): string | null {
  if (pw.length < 8) return "Password must be at least 8 characters.";
  if (!/[A-Z]/.test(pw)) return "Password must contain an uppercase letter.";
  if (!/[a-z]/.test(pw)) return "Password must contain a lowercase letter.";
  if (!/\d/.test(pw)) return "Password must contain a digit.";
  return null;
}

type FieldErrors = Partial<
  Record<
    | "ownerName"
    | "businessName"
    | "email"
    | "password"
    | "confirm"
    | "phone"
    | "storeType"
    | "terms"
    | "form",
    string
  >
>;

// Allowlist of ?next= targets — defends against open-redirect
// (attacker links to /register?next=https://evil.com to phish the
// user post-signup). Only same-origin paths starting with "/" survive,
// AND the path must match one of the known post-signup destinations.
const ALLOWED_NEXT_PATHS = [
  "/onboarding/step/pos",
  "/onboarding/step/delivery",
  "/onboarding/step/menu",
  "/onboarding/step/inventory",
  "/onboarding/step/details",
  "/onboarding/step/activate",
  "/app/dashboard",
] as const;

function safeNextPath(raw: string | null): string {
  if (!raw) return "/onboarding/step/pos";
  if (!raw.startsWith("/")) return "/onboarding/step/pos";
  if (raw.startsWith("//")) return "/onboarding/step/pos"; // protocol-relative
  // Strip query string for the allowlist check, then re-append.
  const [path] = raw.split("?", 1);
  if (!(ALLOWED_NEXT_PATHS as readonly string[]).includes(path)) {
    return "/onboarding/step/pos";
  }
  return raw;
}

export default function Register() {
  useEffect(() => { document.title = "Create Account — PREP POS"; }, []);
  // Remember the plan picked on the marketing site; the Activate step highlights it.
  useEffect(() => { captureSignupPlan(); }, []);
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = safeNextPath(searchParams.get("next"));

  const [ownerName, setOwnerName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [phone, setPhone] = useState("");
  const [storeType, setStoreType] = useState("");
  const [agreedToTerms, setAgreedToTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});

  const validate = (): FieldErrors => {
    const e: FieldErrors = {};
    if (!ownerName.trim()) e.ownerName = "Enter your full name.";
    if (!businessName.trim()) e.businessName = "Enter your business name.";
    if (!email.trim()) e.email = "Enter your email.";
    const pwIssue = passwordIssue(password);
    if (pwIssue) e.password = pwIssue;
    if (password !== confirm) e.confirm = "Passwords do not match.";
    if (!phone.trim()) e.phone = "Enter a contact phone number.";
    if (!storeType) e.storeType = "Select your store type.";
    if (!agreedToTerms) e.terms = "You must agree to the Terms and Privacy Policy.";
    return e;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const v = validate();
    setErrors(v);
    if (Object.keys(v).length > 0) return;

    const signupRef = captureSignupRef();
    setLoading(true);
    try {
      await register({
        store_name: businessName.trim(),
        name: ownerName.trim(),
        email: email.trim(),
        password,
        phone: phone.trim(),
        store_type: storeType,
        // Which email / friend brought this store (best-effort, from ?ref=).
        ...(signupRef ? { signup_ref: signupRef } : {}),
      });
      navigate(nextPath, { replace: true });
    } catch (err) {
      // Surface structured server errors (e.g. "email already registered",
      // or a 429 lockout if the registration endpoint is ever rate-limited)
      // rather than a generic toast. Backend HTTPExceptions are shaped
      // { detail: { error, detail } } — the human string is `detail.detail`.
      // (Pydantic 422s send `detail` as an array; those fall through to the
      // generic message below, which is intended.)
      const ax = err as AxiosError<{ detail?: { detail?: string } | string }>;
      const status = ax.response?.status;
      const detail = ax.response?.data?.detail;
      const serverMsg =
        typeof detail === "string"
          ? detail
          : typeof detail?.detail === "string"
            ? detail.detail
            : undefined;

      let message = "Could not create your account. Please try again.";
      if (status === 409) {
        message = serverMsg ?? "An account with this email already exists.";
        setErrors({ email: message });
      } else if (status === 429) {
        message =
          serverMsg ??
          "Too many attempts. Please try again in a few minutes.";
        setErrors({ form: message });
      } else if (status === 422 && serverMsg) {
        message = serverMsg;
        setErrors({ form: message });
      } else {
        setErrors({ form: message });
      }
      toast({
        variant: "destructive",
        title: "Registration failed",
        description: message,
      });
    } finally {
      setLoading(false);
    }
  };

  // Live hint as the user types their password — renders green when the
  // complexity check passes so the merchant knows it's safe to submit.
  const liveHint = password ? passwordIssue(password) : null;

  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-4 py-12">
      <div className="w-full max-w-md space-y-8">
        {/* Header */}
        <div className="text-center space-y-2">
          <h1 className="text-3xl font-bold tracking-tight text-foreground font-serif">
            Start your 30-day free trial
          </h1>
          <p className="text-muted-foreground">
            {CTA.TRUST_LINE}
          </p>
        </div>

        {/* Card */}
        <div className="bg-card border border-border rounded-2xl p-8 shadow-sm">
          <form onSubmit={handleSubmit} className="space-y-4" noValidate>
            <div className="space-y-2">
              <Label htmlFor="ownerName">Your name</Label>
              <Input
                id="ownerName"
                placeholder="Jane Merchant"
                value={ownerName}
                onChange={(e) => setOwnerName(e.target.value)}
                required
                autoComplete="name"
                aria-invalid={Boolean(errors.ownerName)}
                aria-describedby={errors.ownerName ? "err-ownerName" : undefined}
                className="h-11"
              />
              {errors.ownerName ? (
                <p id="err-ownerName" role="alert" className="text-xs text-destructive">
                  {errors.ownerName}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="businessName">Business name</Label>
              <Input
                id="businessName"
                placeholder="Jane's Deli"
                value={businessName}
                onChange={(e) => setBusinessName(e.target.value)}
                required
                autoComplete="organization"
                aria-invalid={Boolean(errors.businessName)}
                aria-describedby={errors.businessName ? "err-businessName" : undefined}
                className="h-11"
              />
              {errors.businessName ? (
                <p id="err-businessName" role="alert" className="text-xs text-destructive">
                  {errors.businessName}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="regEmail">Email</Label>
              <Input
                id="regEmail"
                type="email"
                placeholder="you@example.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="email"
                aria-invalid={Boolean(errors.email)}
                aria-describedby={errors.email ? "err-email" : undefined}
                className="h-11"
              />
              {errors.email ? (
                <p id="err-email" role="alert" className="text-xs text-destructive">
                  {errors.email}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="regPassword">Password</Label>
              <div className="relative">
                <Input
                  id="regPassword"
                  type={showPassword ? "text" : "password"}
                  placeholder="••••••••"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  aria-invalid={Boolean(errors.password)}
                  aria-describedby={
                    errors.password
                      ? "err-password"
                      : liveHint
                        ? "hint-password"
                        : undefined
                  }
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-muted-foreground hover:text-foreground"
                  aria-label={showPassword ? "Hide password" : "Show password"}
                  aria-pressed={showPassword}
                >
                  {showPassword ? (
                    <EyeOff className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Eye className="h-4 w-4" aria-hidden="true" />
                  )}
                </button>
              </div>
              {errors.password ? (
                <p id="err-password" role="alert" className="text-xs text-destructive">
                  {errors.password}
                </p>
              ) : password && liveHint ? (
                <p id="hint-password" className="text-xs text-muted-foreground">
                  {liveHint}
                </p>
              ) : password ? (
                <p className="text-xs text-emerald-600">
                  Password meets the minimum complexity requirements.
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  8+ characters with an uppercase, lowercase, and digit.
                </p>
              )}
            </div>

            <div className="space-y-2">
              <Label htmlFor="confirm">Confirm password</Label>
              <Input
                id="confirm"
                type={showPassword ? "text" : "password"}
                placeholder="••••••••"
                value={confirm}
                onChange={(e) => setConfirm(e.target.value)}
                required
                autoComplete="new-password"
                aria-invalid={Boolean(errors.confirm)}
                aria-describedby={errors.confirm ? "err-confirm" : undefined}
                className="h-11"
              />
              {errors.confirm ? (
                <p id="err-confirm" role="alert" className="text-xs text-destructive">
                  {errors.confirm}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone">Phone</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="(555) 123-4567"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                required
                autoComplete="tel"
                aria-invalid={Boolean(errors.phone)}
                aria-describedby={errors.phone ? "err-phone" : undefined}
                className="h-11"
              />
              {errors.phone ? (
                <p id="err-phone" role="alert" className="text-xs text-destructive">
                  {errors.phone}
                </p>
              ) : null}
            </div>

            <div className="space-y-2">
              <Label htmlFor="storeType">Store type</Label>
              <Select value={storeType} onValueChange={setStoreType} required>
                <SelectTrigger
                  id="storeType"
                  className="h-11"
                  aria-invalid={Boolean(errors.storeType)}
                  aria-describedby={errors.storeType ? "err-storeType" : undefined}
                >
                  <SelectValue placeholder="Select your store type" />
                </SelectTrigger>
                <SelectContent>
                  {STORE_TYPES.map((type) => (
                    <SelectItem key={type} value={type}>
                      {type}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errors.storeType ? (
                <p id="err-storeType" role="alert" className="text-xs text-destructive">
                  {errors.storeType}
                </p>
              ) : null}
            </div>

            <div className="flex items-start gap-2 pt-1">
              <Checkbox
                id="terms"
                checked={agreedToTerms}
                onCheckedChange={(checked) => setAgreedToTerms(checked === true)}
                aria-invalid={Boolean(errors.terms)}
                aria-describedby={errors.terms ? "err-terms" : undefined}
              />
              <div className="space-y-1">
                <Label htmlFor="terms" className="text-sm font-normal leading-snug">
                  I agree to the{" "}
                  <Link to="/terms" className="text-primary underline">
                    Terms of Service
                  </Link>{" "}
                  and{" "}
                  <Link to="/privacy" className="text-primary underline">
                    Privacy Policy
                  </Link>
                  .
                </Label>
                {errors.terms ? (
                  <p id="err-terms" role="alert" className="text-xs text-destructive">
                    {errors.terms}
                  </p>
                ) : null}
              </div>
            </div>

            {errors.form ? (
              <p role="alert" aria-live="polite" className="text-sm text-destructive">
                {errors.form}
              </p>
            ) : null}

            <Button
              type="submit"
              className="w-full h-12 text-base bg-primary hover:bg-primary/90 mt-2"
              disabled={loading}
            >
              {loading ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : null}
              Create my account &amp; start my free trial
            </Button>
          </form>
        </div>

        {/* Footer */}
        <p className="text-center text-sm text-muted-foreground">
          Already have an account?{" "}
          <Link
            to="/login"
            className="font-medium text-primary hover:underline"
          >
            Sign in
          </Link>
        </p>
      </div>
    </div>
  );
}
